/* eslint-disable @typescript-eslint/no-shadow */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useRouter } from 'next/router';
import { useSearchParams } from 'next/navigation';

import { useMutation } from '@tanstack/react-query';

import { useBusiness } from './business';
import { profileHasPermission } from '../utils/authorization';
import { setAuthToken } from '../services/customAxios';
import { getPublicBusinessBySlug } from '../services/controllers/business';
import {
  authLogin,
  IAuthLogin,
  IAuthSessionResponse,
  refreshToken,
} from '../services/controllers/auth';
import {
  ILoggedUser,
  ILoggedUserConfigurations,
} from '../interfaces/i-logged-user';
import { ILoggedBusiness } from '../interfaces/i-logged-business';
import { IFCWithChildren } from '../interfaces/i-fc-with-children';
import { useCookies } from '../hooks/use-cookies';
import { sidebarRouteKeys } from '../constants/sidebar';
import { cookies } from '../constants/cookies';
import { SplashLoader } from '../components/SplashLoader';

interface IAuthContextData {
  user: ILoggedUser;
  logout: () => void;
  login: (payload: IAuthLogin) => Promise<void>;
  hasPermission: (permissions: string[]) => boolean;
  patchUserConfigurations: (partial: ILoggedUserConfigurations) => void;
}

const AuthContext = createContext({} as IAuthContextData);

const getEmployeeSelfRoute = (employeeId?: string): string =>
  employeeId ? `/funcionarios/eu/${employeeId}` : '/funcionarios/eu';

const REFRESH_TOKEN_BEFORE_EXPIRATION_MS = 2 * 60 * 1000;
const VMCORE_MARKETING_HOST = 'vmcore-erp.com';

const normalizeHost = (host?: string): string =>
  String(host || '')
    .trim()
    .toLowerCase()
    .replace(/^www\./, '')
    .split(':')[0];

const decodeTokenPayload = (token?: string): Record<string, any> => {
  try {
    if (!token) return {};

    const [, payload] = token.split('.');
    if (!payload) return {};

    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
      '='
    );
    return JSON.parse(window.atob(paddedPayload));
  } catch (error) {
    return {};
  }
};

const getTokenExpiresAt = (token?: string): number | null => {
  if (!token) return null;

  try {
    const decoded = decodeTokenPayload(token);

    return decoded.exp ? decoded.exp * 1000 : null;
  } catch (error) {
    return null;
  }
};

const getPathBusinessSlug = (path: string): string | null => {
  const normalizedPath = path.split('?')[0].replace(/\/+$/, '');
  const segments = normalizedPath.split('/').filter(Boolean);

  if (segments.length !== 1) return null;

  const [slug] = segments;
  if (!slug || slug === 'admin' || sidebarRouteKeys.includes(slug)) {
    return null;
  }

  return slug;
};

const isVmcoreMarketingRoot = (path: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    path === '/' &&
    normalizeHost(window.location.host) === VMCORE_MARKETING_HOST
  );
};

const AuthProvider: IFCWithChildren = ({ children }) => {
  const { getItem, setItem, removeItem } = useCookies();
  const { business, setBusiness } = useBusiness();
  const [canShow, setCanShow] = useState<boolean>(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const [user, setUser] = useState<ILoggedUser | null>(null);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAdminRoute = useCallback(
    (path: string): boolean => path === '/admin' || path.startsWith('/admin/'),
    []
  );

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, []);

  const applyAuthenticatedUser = useCallback(
    (authenticatedUser: ILoggedUser) => {
      setUser({
        id: authenticatedUser.id,
        businessId: authenticatedUser.businessId,
        businessSlug: authenticatedUser.businessSlug,
        name: authenticatedUser.name,
        login: authenticatedUser.login,
        profile: authenticatedUser.profile,
        employeeId: authenticatedUser.employeeId,
        configurations: authenticatedUser.configurations || {},
      });
    },
    []
  );

  // Atualiza as configurações do usuário em memória depois de um PATCH
  // bem-sucedido, evitando um refresh de token só para reler o valor novo.
  const patchUserConfigurations = useCallback(
    (partial: ILoggedUserConfigurations) => {
      setUser((previous) =>
        previous
          ? {
              ...previous,
              configurations: {
                ...(previous.configurations || {}),
                ...partial,
              },
            }
          : previous
      );
    },
    []
  );

  const applyAuthenticatedBusiness = useCallback(
    (authenticatedBusiness: ILoggedBusiness | null) => {
      setBusiness(authenticatedBusiness);
    },
    [setBusiness]
  );

  const getLogoutRedirectPath = useCallback((): string => {
    const currentPath = router.asPath.split('?')[0];
    const currentBusinessSlug = getPathBusinessSlug(currentPath);
    const targetBusinessSlug =
      user?.businessSlug || business?.slug || currentBusinessSlug;

    if (targetBusinessSlug === 'monteiro') {
      return '/';
    }

    return targetBusinessSlug ? `/${targetBusinessSlug}` : '/';
  }, [business?.slug, router.asPath, user?.businessSlug]);

  const logout = useCallback(() => {
    clearRefreshTimer();
    setCanShow(false);
    setUser(null);
    setAuthToken('');
    removeItem(cookies.AUTH_TOKEN);
    removeItem(cookies.REFRESH_TOKEN);

    if (isAdminRoute(router.asPath.split('?')[0])) {
      setBusiness(null);
      setCanShow(true);
      return;
    }

    router.replace(getLogoutRedirectPath()).finally(() => {
      setCanShow(true);
    });
  }, [
    clearRefreshTimer,
    getLogoutRedirectPath,
    isAdminRoute,
    removeItem,
    router,
    setBusiness,
  ]);

  const scheduleRefresh = useCallback(
    (accessToken?: string) => {
      clearRefreshTimer();

      const expiresAt = getTokenExpiresAt(accessToken);
      if (!expiresAt) return;

      const refreshIn =
        expiresAt - Date.now() - REFRESH_TOKEN_BEFORE_EXPIRATION_MS;

      refreshTimeoutRef.current = setTimeout(async () => {
        const currentRefreshToken = getItem(cookies.REFRESH_TOKEN);

        if (!currentRefreshToken) {
          logout();
          return;
        }

        try {
          const {
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
            user: refreshedUser,
            business,
          } = await refreshToken(currentRefreshToken);

          setItem(cookies.AUTH_TOKEN, newAccessToken);
          setItem(cookies.REFRESH_TOKEN, newRefreshToken);
          setAuthToken(newAccessToken);
          applyAuthenticatedUser(refreshedUser);
          applyAuthenticatedBusiness(business);
          scheduleRefresh(newAccessToken);
        } catch (error) {
          logout();
        }
      }, Math.max(refreshIn, 0));
    },
    [
      applyAuthenticatedBusiness,
      applyAuthenticatedUser,
      clearRefreshTimer,
      getItem,
      logout,
      setItem,
    ]
  );

  const { mutateAsync: handleLogin } = useMutation({
    mutationFn: async (data: IAuthLogin) => {
      return authLogin(data);
    },
    onSuccess: (response) => {
      const { accessToken, refreshToken, user, business } = response.data;
      setAuthToken(accessToken);
      setItem(cookies.AUTH_TOKEN, accessToken);
      setItem(cookies.REFRESH_TOKEN, refreshToken);
      scheduleRefresh(accessToken);
      applyAuthenticatedUser(user);
      applyAuthenticatedBusiness(business);

      router.push({
        pathname:
          user.employeeId && user.profile.employeeProfile
            ? getEmployeeSelfRoute(user.employeeId)
            : '/estatisticas',
        query: null,
      });
    },
  });

  const hasPermission = useCallback(
    (permissions: string[]): boolean => {
      if (!user || !user.profile) return false;

      return profileHasPermission(user.profile, permissions);
    },
    [user]
  );

  const login = useCallback(
    async (payload: IAuthLogin) => {
      await handleLogin(payload);
    },
    [handleLogin]
  );

  const applyAuthenticatedSession = useCallback(
    ({ accessToken, refreshToken, user, business }: IAuthSessionResponse) => {
      setItem(cookies.AUTH_TOKEN, accessToken);
      setItem(cookies.REFRESH_TOKEN, refreshToken);
      setAuthToken(accessToken);
      scheduleRefresh(accessToken);
      applyAuthenticatedUser(user);
      applyAuthenticatedBusiness(business);
    },
    [
      applyAuthenticatedBusiness,
      applyAuthenticatedUser,
      scheduleRefresh,
      setItem,
    ]
  );

  const isPrivateRoute = (path: string): boolean => {
    return sidebarRouteKeys.some((key) => path.replace('/', '').includes(key));
  };

  const clearAuthSession = useCallback(() => {
    clearRefreshTimer();
    setUser(null);
    setAuthToken('');
    removeItem(cookies.AUTH_TOKEN);
    removeItem(cookies.REFRESH_TOKEN);
  }, [clearRefreshTimer, removeItem]);

  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      const path = router.asPath.split('?')[0];
      if (path.startsWith('/carteirinhas/')) {
        clearRefreshTimer();
        setCanShow(true);
        return;
      }
      if (isAdminRoute(path)) {
        setCanShow(true);
        return;
      }

      if (isVmcoreMarketingRoot(path)) {
        setCanShow(true);
        return;
      }

      const isPrivate = isPrivateRoute(path);
      const token = getItem(cookies.AUTH_TOKEN);
      const storedRefreshToken = getItem(cookies.REFRESH_TOKEN);
      const requestedBusinessSlug = getPathBusinessSlug(path);

      if (isPrivate && !storedRefreshToken) {
        logout();
        return;
      }

      if (!isPrivate && storedRefreshToken) {
        const storedTokenPayload = decodeTokenPayload(storedRefreshToken);

        if (requestedBusinessSlug) {
          try {
            const requestedBusiness = await getPublicBusinessBySlug(
              requestedBusinessSlug
            );
            const storedBusinessSlug = storedTokenPayload.businessSlug || null;
            const storedBusinessId = storedTokenPayload.businessId || null;
            const isSameBusiness =
              storedBusinessSlug === requestedBusiness.slug ||
              storedBusinessId === requestedBusiness.id;

            if (!isSameBusiness) {
              clearAuthSession();
              setCanShow(true);
              return;
            }
          } catch (error) {
            clearAuthSession();
            setCanShow(true);
            return;
          }
        }

        try {
          const refreshed = await refreshToken(storedRefreshToken);

          const queryParams = Object.fromEntries(
            Array.from(searchParams.entries())
          );

          applyAuthenticatedSession(refreshed);

          router.push({
            pathname:
              refreshed.user.employeeId &&
              refreshed.user.profile.employeeProfile
                ? getEmployeeSelfRoute(refreshed.user.employeeId)
                : '/estatisticas',
            query: queryParams,
          });
        } catch (error) {
          removeItem(cookies.AUTH_TOKEN);
          removeItem(cookies.REFRESH_TOKEN);
          setCanShow(true);
        }

        return;
      }

      if (token) {
        setAuthToken(token);
        scheduleRefresh(token);
      }

      // Caso 4: rota pública sem token → libera visualização
      setCanShow(true);
    };

    checkAuth();
  }, [
    clearAuthSession,
    getItem,
    isAdminRoute,
    logout,
    removeItem,
    router,
    scheduleRefresh,
    searchParams,
    setItem,
    applyAuthenticatedSession,
  ]);

  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      const path = router.asPath.split('?')[0];
      if (path.startsWith('/carteirinhas/')) {
        setCanShow(true);
        return;
      }
      if (isAdminRoute(path)) {
        setCanShow(true);
        return;
      }

      if (isVmcoreMarketingRoot(path)) {
        setCanShow(true);
        return;
      }

      const isPrivate = isPrivateRoute(path);
      const storedRefreshToken = getItem(cookies.REFRESH_TOKEN);

      if (isPrivate && !user && storedRefreshToken) {
        setCanShow(false);
        try {
          const refreshed = await refreshToken(storedRefreshToken);
          applyAuthenticatedSession(refreshed);

          if (
            refreshed.user.employeeId &&
            refreshed.user.profile.employeeProfile &&
            !router.asPath.includes('/funcionarios/eu') &&
            path !== '/veiculos/checklist'
          ) {
            router.push(getEmployeeSelfRoute(refreshed.user.employeeId));
          }
        } catch (error) {
          removeItem(cookies.AUTH_TOKEN);
          removeItem(cookies.REFRESH_TOKEN);
          setBusiness(null);
        } finally {
          setCanShow(true);
        }
      }
    };
    checkAuth();
  }, [
    applyAuthenticatedSession,
    getItem,
    isAdminRoute,
    removeItem,
    router,
    scheduleRefresh,
    setBusiness,
    setItem,
    user,
  ]);

  return (
    <AuthContext.Provider
      value={{
        user,
        logout,
        login,
        hasPermission,
        patchUserConfigurations,
      }}
    >
      {canShow ? (
        <>{children}</>
      ) : (
        <SplashLoader loaderColor="neutral.100" showLogo={false} />
      )}
    </AuthContext.Provider>
  );
};

const useAuth = (): IAuthContextData => {
  return useContext(AuthContext);
};

export { AuthProvider, useAuth };
